import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AppButton, AppTextField } from "../../../components/form";
import DataTable from "react-data-table-component";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../../../components/dialog";
import {
  AppFormField,
  AppFormGrid,
  PageDataTablePanel,
  PageFilterPanel,
  PageStatCard,
} from "../../../components/layout";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import {
  businessErrorService,
  type BusinessErrorEvent,
} from "../../../services/businessErrorService";
import { useMaterialUIController } from "../../../context";
import { GRID_FILTER_2, GRID_THIRD, pageStatGridSx } from "../../../lib/ui/formLayout";
import { getReportChatShell } from "../../../lib/ui/reportChatShell";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { buildAuditLogDataTableCustomStyles } from "../../../assets/theme/custom/components/report";

const toIsoUtc = (value: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const formatOccurredAt = (value?: string) =>
  value ? new Date(value).toISOString().replace("T", " ").slice(0, 19) : "-";

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

function BusinessErrorsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const custom = useAppCustomStyles();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<BusinessErrorEvent[]>([]);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState<BusinessErrorEvent | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await businessErrorService.list(toIsoUtc(from), toIsoUtc(to));
      setEvents(response.content || []);
      setSearched(true);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || t("businessErrors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const categorySummary = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of events) {
      const key = item.errorCategory || "UNKNOWN";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  }, [events]);

  const statusSummary = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of events) {
      const key = item.httpStatus || 0;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  }, [events]);

  const reportShell = useMemo(() => getReportChatShell(theme), [theme]);
  const tableShellStyles = useMemo(() => buildAuditLogDataTableCustomStyles(theme), [theme]);

  const columns = useMemo(
    () => [
      {
        id: "occurredAt",
        name: t("businessErrors.colTime"),
        selector: (row: BusinessErrorEvent) => formatOccurredAt(row.occurredAt),
        sortable: true,
        sortFunction: (a: BusinessErrorEvent, b: BusinessErrorEvent) => {
          const ta = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
          const tb = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
          return ta - tb;
        },
        width: "20%",
        grow: 1,
        wrap: true,
      },
      {
        name: t("businessErrors.colCategory"),
        selector: (row: BusinessErrorEvent) => row.errorCategory || "-",
        sortable: true,
        width: "20%",
        grow: 1,
        wrap: true,
      },
      {
        name: t("businessErrors.colMethod"),
        selector: (row: BusinessErrorEvent) => row.requestMethod || "-",
        sortable: true,
        width: "20%",
        grow: 1,
        wrap: true,
      },
      {
        name: t("businessErrors.colStatus"),
        selector: (row: BusinessErrorEvent) =>
          row.httpStatus != null ? String(row.httpStatus) : "-",
        sortable: true,
        sortFunction: (a: BusinessErrorEvent, b: BusinessErrorEvent) =>
          (a.httpStatus || 0) - (b.httpStatus || 0),
        width: "20%",
        grow: 1,
        wrap: true,
      },
      {
        name: t("businessErrors.colCode"),
        selector: (row: BusinessErrorEvent) => row.businessErrorCode || "-",
        sortable: true,
        width: "20%",
        grow: 1,
        wrap: true,
      },
    ],
    [t]
  );

  return (
    <PageListContainer surface="plain">
      <PageHeader
        title={t("businessErrors.title")}
        subtitle={t("businessErrors.subtitle")}
        actions={
          <AppButton
            variant="contained"
            color="dark"
            size="small"
            onClick={load}
            disabled={loading}
          >
            {loading ? t("common.loading") : t("businessErrors.analyze")}
          </AppButton>
        }
      />

      <PageFilterPanel>
        <AppFormField size={GRID_FILTER_2}>
          <AppTextField
            label={t("dateRange.from")}
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </AppFormField>
        <AppFormField size={GRID_FILTER_2}>
          <AppTextField
            label={t("dateRange.to")}
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </AppFormField>
      </PageFilterPanel>

      <AppFormGrid spacing="compact" sx={pageStatGridSx}>
        <AppFormField size={GRID_THIRD}>
          <PageStatCard label={t("businessErrors.totalEvents")} value={events.length} />
        </AppFormField>
        <AppFormField size={GRID_THIRD}>
          <PageStatCard label={t("businessErrors.topCategories")}>
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={custom.pages.shared.statChipStack}>
              {categorySummary.length ? categorySummary.map(([key, count]) => (
                <Chip key={key} size="small" label={`${key}: ${count}`} />
              )) : <Typography variant="body2">-</Typography>}
            </Stack>
          </PageStatCard>
        </AppFormField>
        <AppFormField size={GRID_THIRD}>
          <PageStatCard label={t("businessErrors.topHttpStatus")}>
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={custom.pages.shared.statChipStack}>
              {statusSummary.length ? statusSummary.map(([key, count]) => (
                <Chip key={String(key)} size="small" label={`${key}: ${count}`} />
              )) : <Typography variant="body2">-</Typography>}
            </Stack>
          </PageStatCard>
        </AppFormField>
      </AppFormGrid>

      {error && <Alert severity="error" sx={custom.pages.shared.alertMarginBottom1_5}>{error}</Alert>}

      <PageDataTablePanel borderColor={reportShell.border} backgroundColor={reportShell.appBg}>
        <DataTable
          columns={columns as any}
          data={events}
          progressPending={loading}
          persistTableHead
          pagination
          paginationPerPage={50}
          paginationRowsPerPageOptions={[25, 50, 100, 200]}
          defaultSortFieldId="occurredAt"
          defaultSortAsc={false}
          highlightOnHover
          pointerOnHover
          onRowClicked={(row) => setSelected(row)}
          noDataComponent={
            searched ? (
              <PageEmptyState message={t("businessErrors.noResults")} />
            ) : (
              <PageEmptyState message={t("businessErrors.clickAnalyze")} />
            )
          }
          customStyles={tableShellStyles}
          theme={darkMode ? "dark" : "light"}
        />
      </PageDataTablePanel>

      <AppDialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        <AppDialogTitle>{t("businessErrors.detailTitle")}</AppDialogTitle>
        <AppDialogContent>
          {selected && (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <DetailField label={t("businessErrors.colTime")} value={formatOccurredAt(selected.occurredAt)} />
              <DetailField label={t("businessErrors.colCategory")} value={selected.errorCategory || "-"} />
              <DetailField
                label={t("businessErrors.colStatus")}
                value={selected.httpStatus != null ? String(selected.httpStatus) : "-"}
              />
              <DetailField label={t("businessErrors.colMessage")} value={selected.message || "-"} />
              <DetailField label={t("businessErrors.colCode")} value={selected.businessErrorCode || "-"} />
              <DetailField label={t("businessErrors.colUser")} value={selected.username || "-"} />
              <DetailField label={t("businessErrors.colTenant")} value={selected.tenantId || "-"} />
              <DetailField label={t("businessErrors.colMethod")} value={selected.requestMethod || "-"} />
              <DetailField label={t("businessErrors.colPath")} value={selected.requestPath || "-"} />
              <DetailField label={t("businessErrors.colId")} value={selected.id || "-"} />
            </Stack>
          )}
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={() => setSelected(null)}>
            {t("common.close")}
          </AppDialogCancelButton>
        </AppDialogActions>
      </AppDialog>
    </PageListContainer>
  );
}

export default BusinessErrorsPage;
